"use client";

import { useCallback } from "react";
import { Search } from "lucide-react";
import { useRouter } from "next/navigation";
import { useAppDispatch } from "@/app/lib/features/redux/redux.hook";
import useProductReview from "@/app/lib/features/productReview/productReview.hook";
import { 
  setSearchFilter, 
  setSortFilter, 
  setAppliedFilters 
} from "@/app/lib/features/productReview/productReview.slice";
import { getProductReviewsList as getProductReviewsListThunk } from "@/app/lib/features/productReview/productReview.thunk";
import { ReviewSortOption } from "@/app/lib/features/productReview/productReview.types";
import { trackGeneric } from "@/app/lib/tracking/interactions";
import { Input } from "@/app/components/shadcn/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/components/shadcn/select";

interface SearchAndSortProps {
  placeholder: string;
  sortOptions: ReviewSortOption[];
}

const SearchAndSort = ({ placeholder, sortOptions }: SearchAndSortProps) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { appliedFilters, pendingFilters, currentProductSlug } = useProductReview();
  
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchFilter(e.target.value || undefined));
  }, [dispatch]);

  const handleSearchKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && currentProductSlug) {
      trackGeneric("review_search", "click");
      const params = new URLSearchParams();

      if (pendingFilters.rating && pendingFilters.rating.length > 0) {
        pendingFilters.rating.forEach((r) => params.append("rating", r.toString()));
      }

      if (pendingFilters.topic && pendingFilters.topic.length > 0) {
        pendingFilters.topic.forEach((t) => params.append("topic", t));
      }

      if (pendingFilters.seller_id) {
        params.set("seller_id", pendingFilters.seller_id.toString());
      }

      if (pendingFilters.search) {
        params.set("search", pendingFilters.search);
      }

      if (pendingFilters.sort) {
        params.set("sort", pendingFilters.sort);
      }

      const queryString = params.toString();
      const newUrl = queryString ? `?${queryString}` : window.location.pathname;
      
      // Use router.replace with scroll: false to prevent scrolling to top
      router.replace(newUrl, { scroll: false });

      dispatch(setAppliedFilters(pendingFilters));

      dispatch(
        getProductReviewsListThunk({
          productSlug: currentProductSlug,
          filters: { ...pendingFilters, page: 1, per_page: 15 },
        }),
      );
    }
  }, [pendingFilters, router, dispatch, currentProductSlug]);

  const handleSortChange = useCallback((value: string) => {
    if (!currentProductSlug) return;

    trackGeneric("review_sort", "click");
    dispatch(setSortFilter(value));

    const newFilters = { ...pendingFilters, sort: value };

    const params = new URLSearchParams();

    if (newFilters.rating && newFilters.rating.length > 0) {
      newFilters.rating.forEach((r) => params.append("rating", r.toString()));
    }

    if (newFilters.topic && newFilters.topic.length > 0) {
      newFilters.topic.forEach((t) => params.append("topic", t));
    }

    if (newFilters.seller_id) {
      params.set("seller_id", newFilters.seller_id.toString());
    }

    if (newFilters.search) {
      params.set("search", newFilters.search);
    }

    if (newFilters.sort) {
      params.set("sort", newFilters.sort);
    }

    const queryString = params.toString();
    const newUrl = queryString ? `?${queryString}` : window.location.pathname;
    
    // Use router.replace with scroll: false to prevent scrolling to top
    router.replace(newUrl, { scroll: false });

    dispatch(setAppliedFilters(newFilters));

    dispatch(
      getProductReviewsListThunk({
        productSlug: currentProductSlug,
        filters: { ...newFilters, page: 1, per_page: 15 },
      }),
    );
  }, [pendingFilters, router, dispatch, currentProductSlug]);
  
  return (
    <div className="flex gap-4">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input 
          placeholder={placeholder} 
          className="pl-10" 
          value={pendingFilters.search || ''}
          onChange={handleSearchChange}
          onKeyPress={handleSearchKeyPress}
        />
      </div>
      <Select 
        value={appliedFilters.sort || sortOptions[0]?.key} 
        onValueChange={handleSortChange}
      >
        <SelectTrigger className="w-48">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {sortOptions.map((option) => (
            <SelectItem key={option.key} value={option.key}>
              {option.text}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default SearchAndSort;